import React, { useEffect, useRef, useState } from "react";
import { gsap, Sine } from "gsap/all";
import { Application, Graphics } from "pixi.js-legacy";
import { ChallengeConfig, SolvedChallenge } from "../../models/Types";
import ChallengeUtils from "./ChallengeUtils";
import { useTranslation } from "react-i18next";
import { FederatedPointerEvent } from "pixi.js";
import { isDarkModeEnabled } from "../Util";

export const RECT_HEIGHT = 16;
const RECT_WIDTH = 50;
const ARROW_PADDING = 12;

const ARROW_COLOR_DARK = 0xfa528e;
const ARROW_COLOR_LIGHT = 0xe7608e;
const BEAM_COLOR_DARK = 0x6d4aff;
const BEAM_COLOR_LIGHT = 0x6d4aff;

type Beam = {
  graphics: Graphics;
  dragging: boolean;
};
type Arrow = {
  graphics: Graphics;
  yOffsetToBeam: number;
  animation: GSAPTween;
};

export function Challenge1D(props: ChallengeConfig) {
  const challengeRef = useRef<any>(null);
  const [visualSolution, setVisualSolution] = useState<SolvedChallenge | null>(
    null,
  );
  const { t } = useTranslation() as any;
  const beamRef = useRef<Beam>({ graphics: new Graphics(), dragging: false });
  const dragDeltaY = useRef<number>(0);
  const isProcessing = visualSolution !== null;
  const arrows = useRef<Arrow[]>([]);

  function render1DPuzzle(application: Application) {
    const beamGraphics = beamRef.current.graphics;

    beamGraphics.beginFill(
      isDarkModeEnabled() ? BEAM_COLOR_DARK : BEAM_COLOR_LIGHT,
    );
    beamGraphics.x = props.width - RECT_WIDTH;
    beamGraphics.y = (props.height - RECT_HEIGHT) / 2;
    beamGraphics.drawRect(0, 0, RECT_WIDTH, RECT_HEIGHT);
    beamGraphics.endFill();

    beamGraphics.eventMode = "dynamic";
    beamGraphics.cursor = "grab";

    application.stage.addChild(beamGraphics);

    arrows.current = ["up", "down"].map((dir) => {
      const angle = dir === "up" ? 90 : -90;
      const yOffsetToBeam =
        dir === "up" ? -ARROW_PADDING : RECT_HEIGHT + ARROW_PADDING;

      const arrowGraphics = new Graphics();
      arrowGraphics.x = props.width - RECT_WIDTH / 2;
      arrowGraphics.y = beamGraphics.y + yOffsetToBeam;
      arrowGraphics.beginFill(
        isDarkModeEnabled() ? ARROW_COLOR_DARK : ARROW_COLOR_LIGHT,
      );
      ChallengeUtils.drawPolygon(arrowGraphics, 0, 0, 3, 10, angle);
      arrowGraphics.alpha = 1.0;
      arrowGraphics.endFill();

      const animation = gsap.to(arrowGraphics, {
        alpha: 0.4,
        ease: Sine.easeInOut,
        duration: 1.2,
        repeat: -1,
        yoyo: true,
      });

      const arrow: Arrow = {
        graphics: arrowGraphics,
        yOffsetToBeam,
        animation,
      };

      return arrow;
    });

    function onDragMove(e: FederatedPointerEvent) {
      if (beamRef.current.dragging) {
        const newPosition = e.globalY - dragDeltaY.current;
        if (newPosition > 0 && newPosition < props.height - RECT_HEIGHT) {
          beamRef.current.graphics.y = e.globalY - dragDeltaY.current;
        }
        removeArrows();
      }
    }

    arrows.current.forEach((arrow: Arrow) =>
      application.stage.addChild(arrow.graphics),
    );

    beamGraphics.on("pointerdown", function (e: FederatedPointerEvent) {
      e.stopPropagation();
      dragDeltaY.current = e.globalY - beamGraphics.y;
      beamRef.current.dragging = true;
      beamGraphics.cursor = "grabbing";
    });

    for (const p of ["pointerup", "pointerupoutside"]) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      beamGraphics.on(p, function (e: FederatedPointerEvent) {
        e.stopPropagation();
        dragDeltaY.current = 0;
        beamRef.current.dragging = false;
        beamGraphics.cursor = "grab";
      });
    }

    beamGraphics.on("pointermove", function (e: FederatedPointerEvent) {
      e.stopPropagation();
      onDragMove(e);
    });

    beamGraphics.on("globalpointermove", function (e: FederatedPointerEvent) {
      e.stopPropagation();
      onDragMove(e);
    });
  }

  function removeArrows() {
    arrows.current.forEach((arrow: Arrow) => {
      arrow.graphics.y = beamRef.current.graphics.y + arrow.yOffsetToBeam;
      arrow.animation.kill();
      gsap.to(arrow.graphics, {
        alpha: 0,
        ease: Sine.easeIn,
        duration: 0.05,
      });
    });
  }

  function initialiseApplication() {
    const view = challengeRef.current;

    if (view && view.children.length === 0) {
      const application: Application = ChallengeUtils.generateApplication({
        width: props.width,
        height: props.height,
      });

      view.appendChild(application.view);
      const background = ChallengeUtils.timedCreateSprite(
        props.bgImageURL,
        props.onBgLoadStart,
        props.onBgLoadEnd,
      );
      application.stage.addChild(background);
      render1DPuzzle(application);
    }
  }

  function commitVisualSolution() {
    if (!isProcessing) {
      setVisualSolution({ y: beamRef.current.graphics.y });
    }
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (isProcessing) {
      return;
    } else if (e.code === "Enter" || e.code === "Space") {
      commitVisualSolution();
    } else if (e.code === "ArrowUp" || e.code === "ArrowDown") {
      const sign = e.code === "ArrowUp" ? -2 : 2;
      const graphicsToMove = [
        beamRef.current.graphics,
        ...arrows.current.map((a) => a.graphics),
      ];
      for (const g of graphicsToMove) {
        const newPosition = g.y + sign;
        if (newPosition > 0 && newPosition < props.height - RECT_HEIGHT) {
          g.y = g.y + sign;
          gsap.to(g, {
            y: g.y + sign,
            duration: 0.1,
          });
        }
      }
      removeArrows();
      return true;
    }
  }

  function submitVisualSolution() {
    if (visualSolution !== null) {
      props.submitVisualSolutionCallback(visualSolution);
    }
  }

  useEffect(submitVisualSolution, [visualSolution]);

  function attachDomEvents() {
    document.addEventListener("keydown", handleKeyDown, false);

    const cleanup = () => {
      document.removeEventListener("keydown", handleKeyDown, false);
    };
    return cleanup;
  }

  useEffect(attachDomEvents, [challengeRef]);
  useEffect(initialiseApplication, [challengeRef]);

  return (
    <div>
      <div ref={challengeRef} className="challenge-canvas" />
      {!isProcessing && (
        <button
          className="btn rounded-full btn-small btn-solid-purple btn-full-width"
          onClick={commitVisualSolution}
        >
          {t("Next.text")}
        </button>
      )}
    </div>
  );
}
